import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

/**
 * Tracks the in-flight route transition so UI can show a "pending" state
 * before the new route's `model()` resolves. Ember's built-in
 * `aria-current="page"` only flips AFTER the transition settles, leaving
 * sidebar links visually unresponsive while async `model()` hooks run.
 */
export default class RouterTransitionService extends Service {
  @service declare router: RouterService;

  @tracked pendingRouteName: string | null = null;

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.router.on('routeWillChange', this.handleRouteWillChange);
    this.router.on('routeDidChange', this.handleRouteDidChange);
  }

  willDestroy(): void {
    this.router.off('routeWillChange', this.handleRouteWillChange);
    this.router.off('routeDidChange', this.handleRouteDidChange);
    super.willDestroy();
  }

  handleRouteWillChange = (transition: Transition): void => {
    this.pendingRouteName = transition.to?.name ?? null;
  };

  handleRouteDidChange = (): void => {
    this.pendingRouteName = null;
  };

  /**
   * True when the in-flight transition's target matches `route` (or any of
   * the space-separated patterns in `currentWhen`). Match semantics mirror
   * Ember's link-to: equal OR prefix-with-dot, so `spaces.maps.drafts.show`
   * matches a target of `spaces.maps.drafts`.
   */
  isPendingForRoute(route: string, currentWhen?: string): boolean {
    const pending = this.pendingRouteName;
    if (!pending) return false;
    const candidates = currentWhen ? currentWhen.split(/\s+/).filter(Boolean) : [route];
    return candidates.some((candidate) => pending === candidate || pending.startsWith(`${candidate}.`));
  }
}

declare module '@ember/service' {
  interface Registry {
    'router-transition': RouterTransitionService;
  }
}
